'use client';

import Link from "next/link"
import { Button } from "@/components/ui/button"
import { DsuApplicationFormCompact } from "@/modules/student/components/dsu-application-form-compact"
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  HeartPulse,
  Clock3,
  GraduationCap,
  Users2,
  MapPin,
  BadgeDollarSign,
  FileBadge,
  Plus,
  Minus,
} from "lucide-react"
import { Animated, StaggerContainer } from '@/components/ui/animated'

export default function DsuApplicationPage() {
  const heroHighlights = [
    {
      icon: BadgeDollarSign,
      title: "Discounted rate",
      description: "Exclusive $12.50/hr pricing for verified DSU PMHNP students.",
    },
    {
      icon: Clock3,
      title: "Flexible scheduling",
      description: "We align rotation timing with your DSU cohort calendar.",
    },
    {
      icon: HeartPulse,
      title: "Curated preceptors",
      description: "Pre-vetted PMHNP preceptors matched to your clinical goals.",
    },
  ]

  const valueCards = [
    {
      icon: Sparkles,
      title: "Concierge onboarding",
      description: "A dedicated coordinator walks you through documentation, requirements, and timelines.",
    },
    {
      icon: FileBadge,
      title: "Documents handled once",
      description: "We collect your paperwork up front and keep it ready for future rotations.",
    },
    {
      icon: Users2,
      title: "Personalized matching",
      description: "We pair you with PMHNP preceptors whose practice fits your learning goals.",
    },
    {
      icon: MapPin,
      title: "Local or telehealth",
      description: "Choose in-person rotations near you or virtual placements when it fits.",
    },
  ]

  const howItWorks = [
    {
      title: "Share your details",
      description: "Tell us your cohort, specialty focus, and rotation timing preferences.",
    },
    {
      title: "Match with a PMHNP preceptor",
      description: "We introduce a vetted preceptor that fits your goals and availability.",
    },
    {
      title: "Start your rotation",
      description: "We coordinate paperwork, scheduling, and check-ins so you can focus on learning.",
    },
  ]

  const faqs = [
    {
      q: "Who is eligible for the discounted rate?",
      a: "Active Delaware State University PMHNP students in good academic standing qualify for the $12.50/hr partnership rate.",
    },
    {
      q: "How long does matching take?",
      a: "Most students hear back within a few business days. Timing depends on specialty, location, and your preferred start window.",
    },
    {
      q: "How does the discounted rate work?",
      a: "The partnership rate is applied automatically once we verify your DSU enrollment. There are no hidden fees or surcharges.",
    },
    {
      q: "What if I need to reschedule a rotation?",
      a: "Let your coordinator know as early as possible. We'll work with your preceptor to adjust the schedule when feasible.",
    },
  ]

  return (
    <div className="flex flex-col bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-cyan-700 text-white">
        <div className="absolute inset-0">
          <div className="absolute -top-24 -right-12 h-80 w-80 rounded-full bg-teal-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-10 h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.12),transparent_40%)]" />
        </div>

        <div className="section-inner relative grid items-center gap-12 py-20 lg:grid-cols-[1.05fr,0.95fr]">
          <Animated animation="fade-right" duration={800}>
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-teal-50 ring-1 ring-white/20">
                <GraduationCap className="h-4 w-4" />
                DSU PMHNP Partnership
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl">
                  Discounted PMHNP rotations for DSU students
                </h1>
                <p className="max-w-2xl text-lg text-teal-50/90">
                  American Clinical Experience partners with Delaware State University to offer psychiatric mental health
                  NP rotations at a discounted rate of <span className="font-semibold text-white">$12.50/hr</span>. We work
                  alongside the DSU team — including Dr. Rosemarie White — to manage preceptor onboarding, application
                  processing, and rotation coordination.
                </p>
              </div>

              <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-4 sm:grid-cols-2">
                {heroHighlights.map((item) => {
                  const Icon = item.icon
                  return (
                    <div
                      key={item.title}
                      className="flex gap-3 rounded-xl bg-white/5 p-4 ring-1 ring-white/10 backdrop-blur hover:bg-white/10 transition-colors duration-300"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="text-xs text-teal-50/80">{item.description}</p>
                      </div>
                    </div>
                  )
                })}
              </StaggerContainer>

              <div className="flex flex-wrap items-center gap-4">
                <Link href="#form">
                  <Button className="bg-white px-5 py-2.5 text-emerald-800 shadow-lg shadow-emerald-900/20 hover:bg-emerald-50 hover:scale-105 transition-transform">
                    Apply now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="flex items-center gap-3 text-sm text-teal-50/80">
                  <ShieldCheck className="h-4 w-4" />
                  Exclusive partnership pricing for DSU students.
                </div>
              </div>
            </div>
          </Animated>

          {/* Form */}
          <Animated animation="fade-left" delay={200} duration={800}>
            <div
              id="form"
              className="rounded-2xl border border-white/20 bg-white text-slate-900 shadow-2xl shadow-emerald-900/30"
            >
              <div className="space-y-2 border-b border-slate-100 px-8 py-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                  <GraduationCap className="h-4 w-4" />
                  DSU student application
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Tell us about your rotation needs</h2>
                <p className="text-sm text-slate-600">
                  Share your details and timeline. We&apos;ll match you with a vetted PMHNP preceptor at the partnership rate.
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <GraduationCap className="h-3.5 w-3.5" />
                    Delaware State University
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <HeartPulse className="h-3.5 w-3.5" />
                    PMHNP track
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Concierge support
                  </span>
                </div>
              </div>
              <div className="px-8 py-6">
                <DsuApplicationFormCompact />
              </div>
            </div>
          </Animated>
        </div>
      </section>

      {/* Value Section */}
      <section className="bg-white py-16">
        <div className="section-inner space-y-10">
          <Animated animation="fade-up">
            <div className="flex flex-col gap-3 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">What you get</p>
              <h2 className="text-3xl font-bold text-slate-900">A smoother way to find your PMHNP rotation</h2>
              <p className="mx-auto max-w-3xl text-base text-slate-600">
                From paperwork to ongoing check-ins, we keep the logistics light so you can focus on your clinical learning.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {valueCards.map((item) => {
              const Icon = item.icon
              return (
                <div
                  key={item.title}
                  className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-emerald-900/5 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-3 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.description}</p>
                </div>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner space-y-12">
          <Animated animation="fade-up">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">How it works</p>
                <h2 className="mt-2 text-3xl font-bold text-slate-900">From application to rotation in three steps</h2>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  We keep you informed at every stage and handle the coordination so you can focus on your studies.
                </p>
              </div>
              <Link href="#form">
                <Button variant="outline" className="border-emerald-200 text-emerald-800 hover:bg-emerald-50 hover:scale-105 transition-transform">
                  Start your application
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </Animated>

          <StaggerContainer staggerDelay={150} animation="fade-up" className="grid gap-6 md:grid-cols-3">
            {howItWorks.map((step, index) => (
              <div
                key={step.title}
                className="relative rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm shadow-emerald-900/5 hover:shadow-lg transition-all duration-300"
              >
                <div className="absolute -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-900/20">
                  {index + 1}
                </div>
                <div className="mt-4 space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gray-50 py-16">
        <div className="section-inner max-w-3xl space-y-8">
          <Animated animation="fade-up">
            <div className="space-y-2 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Questions</p>
              <h2 className="text-3xl font-bold text-slate-900">Common questions from DSU students</h2>
              <p className="text-sm text-slate-600">
                Still unsure? Reach out after you apply and we&apos;ll tailor the process to your needs.
              </p>
            </div>
          </Animated>
          <StaggerContainer staggerDelay={100} animation="fade-up" className="space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-xl border border-slate-200 bg-white p-5 shadow-sm shadow-emerald-900/5 hover:shadow-md transition-all duration-300"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-sm font-semibold text-slate-900 transition-colors group-open:text-emerald-700">
                  {faq.q}
                  <span className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 text-slate-500 group-open:border-emerald-200 group-open:bg-emerald-50 group-open:text-emerald-700 transition-all duration-300">
                    <Plus className="h-3 w-3 group-open:hidden" />
                    <Minus className="hidden h-3 w-3 group-open:block" />
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600 animate-in fade-in slide-in-from-top-2 duration-300">{faq.a}</p>
              </details>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-r from-emerald-700 to-teal-600 py-16 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.15),transparent_40%)]" />
        <div className="section-inner relative flex flex-col items-center gap-4 text-center">
          <Animated animation="fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-100">Ready to start your rotation?</p>
          </Animated>
          <Animated animation="fade-up" delay={100}>
            <h2 className="text-3xl font-bold leading-tight text-white md:text-4xl">
              Discounted PMHNP rotations, designed for DSU students
            </h2>
          </Animated>
          <Animated animation="fade-up" delay={200}>
            <p className="max-w-3xl text-base text-teal-50/90">
              Tell us your preferences and we&apos;ll match you with a vetted preceptor on your timeline.
            </p>
          </Animated>
          <Animated animation="scale" delay={300}>
            <Link href="#form">
              <Button className="bg-white text-emerald-800 hover:bg-emerald-50 hover:scale-105 transition-transform">
                Start your application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Animated>
        </div>
      </section>
    </div>
  )
}
